import {
  ApplicationInitStatus,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { filter, take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth/auth.service';
import { LanguageService } from './services/language.service';
import { NotificationService } from './services/notification.service';
import { MainNavComponent } from './shared/components/main-nav/main-nav.component';
import { UserService } from './student/services/user.service';
import { HomePageComponent } from './home/components/home-page/home-page.component';
import { LoaderService } from './shared/services/loader.service';
import { CourseContentService } from './course/services/course-content.service';
import { CourseLectureModel } from './course/models';
import { LanguageEnum } from './shared/models/item.model';
import { IdentityService } from './@core/services/identity.service';
import { IdentityModel } from './@core/models/identity.model';
import { BreadcrumbService } from './@core/services/breadcrumb.service';
declare var $: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [HomePageComponent],
})
export class AppComponent implements OnInit {
  @Output() newItemEvent = new EventEmitter<any>();
  currentSessions: CourseLectureModel[] = [];
  language = false;
  config: any;
  bgvar: boolean;
  isLogin: boolean;
  isContent: boolean;
  courseLink: string;
  title: string;
  isRtl: boolean;
  isSearch: boolean = false;
  // Unified Code shell (digital stamp, breadcrumb); Custom mode renders neither.
  unified: boolean = false;
  identityModel: IdentityModel = {} as IdentityModel;
  showBreadcrumb: boolean = false;
  showPageFeedback: boolean = false;

  @ViewChild(MainNavComponent)
  public mainNavComponent: MainNavComponent;

  constructor(
    private loadingService: LoaderService,
    private langService: LanguageService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private userService: UserService,
    private contentService: CourseContentService,
    private identityService: IdentityService,
    // Injected here so the breadcrumb service listens to the router from the first navigation.
    private breadcrumbService: BreadcrumbService,
    private appInitStatus: ApplicationInitStatus
  ) {
    this.identityModel = this.identityService.getIdentityModel() ?? ({} as IdentityModel);
    this.unified = this.identityService.isUnifiedCode();
    this.showStartupLoaderUntilReady();
    this.langService.bgvarSubject.subscribe((data) => {
      this.bgvar = data;
    });

    if (localStorage.getItem('exab-lang') === 'en') {
      this.langService.toggle();
    } else {
      this.translate.use('ar');
    }

    if (this.router.url == '/course') {
      this.langService.togglebg(true);
    }
  }

  ngOnInit(): void {
    this.isRtl = this.langService.getCurrentLanguage() === LanguageEnum.ar;
    this.langService.languageChange.subscribe((result: LanguageEnum) => {
      this.isRtl = result === LanguageEnum.ar;
    });
    
    this.langService.language.subscribe(
      (value: boolean) => (this.language = value)
    );

    this.langService.bgvarSubject.subscribe((data) => {
      this.bgvar = data;
    });
    if (this.router.url.includes('course')) {
      this.langService.togglebg(true);
    }

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (this.router.url !== '/') {
          this.langService.togglebg(false);
        }

        this.isLogin = e.url.includes('login');
        this.isContent = e.url.includes('lecture') || e.url.includes('exam');
        const path = e.urlAfterRedirects.split(/[?#]/)[0];
        // The course player and the exam pages have their own header bar; they render <app-breadcrumb> below it.
        this.showBreadcrumb = !this.isLogin && !this.isContent && path !== '/' && path !== '/home';
        // Unified Code criterion 40: page feedback, not on sign-in, lecture/exam or checkout screens.
        this.showPageFeedback = this.unified && !this.isLogin && !this.isContent && !path.includes('check-out');
        if (this.isContent) {
          this.courseLink = `/course/details/${this.route.snapshot.paramMap['id']}`;
          this.title = this.route.snapshot.paramMap['title'];
        }
      }
    });

    if (this.authService.isAuthenticated()) {
      this.getCurrentSession();
    }

    this.requestPushNotificationPermission();
  }

  getCurrentSession(): void {
    this.contentService
      .getCurrentSession()
      .subscribe((result: CourseLectureModel[]) => {
        const now = new Date().getTime();
        this.currentSessions = (result || []).filter(session => {
          if (!session.date) return false;
          const sessionStart = new Date(session.date).getTime();
          // Session is considered active from 10 minutes before start until 2 hours after start
          const sessionEnd = sessionStart + (120 * 60 * 1000);
          return now >= (sessionStart - 10 * 60 * 1000) && now <= sessionEnd;
        });
      });
  }

  /**
   * Startup overlay: shown from bootstrap until the first navigation has settled (NavigationEnd, NavigationCancel
   * or NavigationError) and the APP_INITIALIZERs (identity/branding, auth) have finished. Angular creates the root
   * component only after those initialisers resolve, so `donePromise` is already settled here in practice; it is
   * awaited (resolve or reject) to make the dependency explicit. The loader therefore never waits on a timer and
   * cannot stay up after a failed first navigation.
   */
  private showStartupLoaderUntilReady(): void {
    this.loadingService.setLoading(true);
    this.router.events
      .pipe(
        filter((event: RouterEvent) =>
          event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
        take(1)
      )
      .subscribe(() => {
        const hide = () => this.loadingService.setLoading(false);
        this.appInitStatus.donePromise.then(hide, hide);
      });
  }

  public changeLanguage(): void {
    this.langService.toggle();
    console.log(this.language);
  }

  /** Skip link: moves focus to the main landmark without letting the router handle the fragment. */
  skipToMainContent(event: Event): void {
    event.preventDefault();
    const main = document.getElementById('main-content');
    main?.focus();
    main?.scrollIntoView();
  }

  requestPushNotificationPermission() {
    if (
      (document.referrer.includes('login') ||
        document.referrer.includes('register') ||
        this.authService.getFirebaseToken() === null) &&
      this.authService.isAuthenticated()
    ) {
      this.userService.getUserFirebaseToken().subscribe((result: any) => {
        this.notificationService.requestPermission(result);
      });
    }
  }
}
